import React, { useEffect, useMemo, useState } from "react";
import API from "../API";
import { JYSK_ORDER_TYPES } from "../utils/jyskOrderTypes";
import {
  jyskFetch,
  ref,
  money,
  date,
  services,
  statuses,
  payer,
  downloadCsv,
} from "../utils/jyskFinance";
import s from "../styles/JyskWorkflow.module.css";
const purposes = Object.fromEntries(
  JYSK_ORDER_TYPES.map((p) => [p.value, p.label]),
);
const methods = {
  0: "Online ödeme",
  1: "Nakit",
  2: "Online ödeme",
  5: "Teslimatta online ödeme",
  6: "JYSK hesabı",
};
const smsTypes = {
  invitation: "Teslimat formu",
  form_received: "Talep alındı",
  stock_ready: "Stok geldi · Sipariş bağlantısı",
  order_created: "Sipariş oluşturuldu",
};
const show = (value) =>
  value === null || value === undefined || value === "" ? "—" : String(value);
const Badge = ({ children }) => <span className={s.badge}>{children}</span>;
const Summary = ({ rows }) => (
  <dl className={s.summary}>
    {rows.map(([label, value]) => (
      <React.Fragment key={label}>
        <dt>{label}</dt>
        <dd>{value ?? "—"}</dd>
      </React.Fragment>
    ))}
  </dl>
);
function Table({ headers, rows }) {
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [rows]);
  return (
    <>
      <div className={s.table}>
        <table>
          <thead>
            <tr>
              {headers.map((h) => (
                <th key={h}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(page * 25, (page + 1) * 25).map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && (
          <p className={s.muted}>Seçilen filtrelere uygun kayıt bulunamadı.</p>
        )}
      </div>
      {rows.length > 25 && (
        <div
          className={s.row}
          style={{
            marginTop: 16,
          }}
        >
          <button
            className={s.secondary}
            disabled={!page}
            onClick={() => setPage((p) => p - 1)}
          >
            Önceki
          </button>
          <span>
            {page + 1} / {Math.ceil(rows.length / 25)}
          </span>
          <button
            className={s.secondary}
            disabled={(page + 1) * 25 >= rows.length}
            onClick={() => setPage((p) => p + 1)}
          >
            Sonraki
          </button>
        </div>
      )}
    </>
  );
}
const tabsForRole = (role) =>
  role === "finance"
    ? [
        ["overview", "Genel bakış"],
        ["finance", "Finansal raporlar"],
        ["orders", "Siparişler"],
        ["requests", "Teslimat talepleri"],
        ["invoices", "Faturalar ve ödemeler"],
      ]
    : [
        ["orders", "Siparişlerim"],
        ["requests", "Teslimat talepleri"],
        ["sms", "SMS gönderim geçmişi"],
        ["invite", "SMS gönder"],
      ];

export default function JyskFinancePortal() {
  const [ctx, setCtx] = useState(null);
  const [overview, setOverview] = useState(null);
  const [tab, setTab] = useState("requests");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    from: "",
    to: "",
    store: "",
    city: "",
    status: "",
    payer: "",
    payment: "",
    purpose: "",
    service: "",
    stockStatus: "",
    dateBy: "created",
  });
  const [report, setReport] = useState("stores");
  const [invoiceStatus, setInvoiceStatus] = useState("");
  const [invoicePeriod, setInvoicePeriod] = useState("");
  const run = async (fn) => {
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const load = async (next, page = 1) => {
    if (next === "overview") { setOverview(await jyskFetch("overview")); return; }
    const query =
      next === "orders" || next === "finance"
        ? filters
        : {
            page,
            limit: 25,
            search,
            payer: filters.payer,
            stockStatus: filters.stockStatus,
          };
    const data = await jyskFetch(next === "finance" ? "orders" : next, query);
    setRows(data.rows || []);
    setPagination(data.pagination || null);
  };
  const tabStorageKey = () =>
    `jysk-management-tab:${API.user?.id || API.user?.email || ""}`;
  useEffect(() => {
    let cancelled = false;
    let initialized = false;
    let prompting = false;
    const initialize = () => {
      if (cancelled || initialized) return;
      if (!API.user) {
        if (!prompting) {
          prompting = true;
          global.promptBusinessLogin?.(() => {
            prompting = false;
            initialize();
          });
        }
        return;
      }
      initialized = true;
      run(async () => {
        const context = { ...await jyskFetch("context"), role: "finance", canConvert: false };
        if (cancelled) return;
        setCtx(context);
        let saved;
        try {
          saved = sessionStorage.getItem(tabStorageKey());
        } catch {}
        const options = tabsForRole(context.role);
        const initial = options.some(([key]) => key === saved)
          ? saved
          : options[0][0];
        setTab(initial);
        if (initial !== "invite") await load(initial);
      });
    };
    const ready = (event) => {
      if (cancelled) return;
      if (event.detail?.error) {
        setError(event.detail.error);
        return;
      }
      initialize();
    };
    window.addEventListener("tasimacim-login-ready", ready);
    document.title = "JYSK Finans · Taşımacım";
    initialize();
    return () => {
      cancelled = true;
      window.removeEventListener("tasimacim-login-ready", ready);
    };
  }, []);
  const changeTab = (next) => {
    try {
      sessionStorage.setItem(tabStorageKey(), next);
    } catch {}
    setRows([]);
    setPagination(null);
    setTab(next);
    setDetail(null);
    setSearch("");
    setNotice("");
    if (next !== "invite") run(() => load(next));
  };
  const openRequest = (id) =>
    run(async () => {
      const data = await jyskFetch("request", {
        id,
      });
      setDetail({
        kind: "request",
        value: data.request,
      });
    });
  const openOrder = (id) =>
    run(async () => {
      const data = await jyskFetch("orders", {
        id,
      });
      if (!data.rows[0]) throw new Error("Sipariş bulunamadı.");
      setDetail({
        kind: "order",
        value: data.rows[0],
      });
    });
  const [smsConfirmation, setSmsConfirmation] = useState(null);
  const updateRequest = (extra, confirmed = false) => {
    const request = detail.value;
    const stockReady = (extra.stockStatus || request.stockStatus) === "stock_arrived";
    const unresolved = (request.productIssues || []).some(issue => issue.status !== "resolved" && issue.id !== extra.resolveIssue);
    const sending = request.requestFlowVersion === 2 && request.paymentResponsibility === "customer" &&
      stockReady && !unresolved && (extra.stockStatus === "stock_arrived" || extra.resolveIssue !== undefined);
    if (sending && !confirmed) {
      setSmsConfirmation({ extra, message: extra.stockStatus === "stock_arrived"
        ? "Bu talep Stok Hazır olarak işaretlenecek ve müşteriye SMS ile sipariş oluşturma bağlantısı gönderilecektir."
        : "Ürün kontrolleri tamamlandı. Müşteriye SMS ile sipariş oluşturma bağlantısı gönderilecektir." });
      return;
    }
    return run(async () => {
      const updateResult = await jyskFetch("update", {
        id: detail.value._id,
        ...extra,
        confirmSendSms: confirmed,
      });
      const data = await jyskFetch("request", {
        id: detail.value._id,
      });
      setDetail({
        kind: "request",
        value: data.request,
      });
      setNotice(updateResult.smsSent ? "Talep güncellendi ve sipariş bağlantısı SMS ile gönderildi." : "Talep güncellendi.");
    });
  };
  const convert = () =>
    run(async () => {
      const customerPaid = detail.value.paymentResponsibility === "customer";
      if (
        customerPaid &&
        !window.confirm(
          "Müşteriye SMS ile sipariş oluşturma bağlantısı gönderilecektir. Devam edilsin mi?",
        )
      )
        return;
      const data = await jyskFetch(customerPaid ? "send-checkout" : "convert", {
        id: detail.value._id,
      });
      if (data.smsSent) {
        setNotice("Sipariş bağlantısı SMS gönderim geçmişine kaydedildi.");
        return;
      }
      const destination = new URL(data.url);
      window.location.assign(destination.pathname + destination.search);
    });
  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!search ||
            JSON.stringify([
              r._id,
              r.full_name,
              r.phone,
              r.store_label,
              r.to?.contactName,
              r.message,
            ])
              .toLocaleLowerCase("tr")
              .includes(search.toLocaleLowerCase("tr"))) &&
          (tab !== "requests" ||
            ((!filters.payer || r.paymentResponsibility === filters.payer) &&
              (!filters.stockStatus || r.stockStatus === filters.stockStatus))),
      ),
    [rows, search, filters, tab],
  );
  const financial = filtered.filter((o) => o.status !== -1);
  const total = financial.reduce(
    (n, o) => n + Number(o.pricing?.price || 0),
    0,
  );
  const groups = useMemo(() => {
    const map = new Map();
    financial.forEach((o) => {
      const key =
        report === "purpose"
          ? o.orderPurpose || o.orderType || "other"
          : report === "service"
            ? o.service
            : o.storeId || "—";
      const entry = map.get(key) || {
        key,
        count: 0,
        total: 0,
        account: 0,
        customer: 0,
      };
      const amount = Number(o.pricing?.price || 0);
      entry.count++;
      entry.total += amount;
      entry[o.payer === "account" ? "account" : "customer"] += amount;
      map.set(key, entry);
    });
    return [...map.values()];
  }, [financial, report]);
  const invoices = filtered.filter(
    (i) =>
      (!invoiceStatus || i.paymentStatus === invoiceStatus) &&
      (!invoicePeriod || i.period === invoicePeriod),
  );
  const exportReport = () => {
    if (tab === "invoices")
      return downloadCsv(
        "JYSK-Faturalar",
        [
          "Fatura",
          "Dönem",
          "Siparişler",
          "Fatura Tarihi",
          "Vade",
          "Tutar",
          "Durum",
          "Ödeme Tarihi",
          "Referans",
        ],
        invoices.map((i) => [
          i._id,
          i.period,
          i.orderIds.map(ref).join(" / "),
          i.issued,
          i.due,
          (i.amountCents / 100).toFixed(2),
          statuses[i.paymentStatus],
          i.payment?.date,
          i.payment?.reference,
        ]),
      );
    if (tab === "finance" && report !== "orders")
      return downloadCsv(
        "JYSK-Rapor",
        [
          report === "purpose"
            ? "Sipariş Amacı"
            : report === "service"
              ? "Hizmet"
              : "Mağaza",
          "Sipariş Sayısı",
          "Toplam",
          "JYSK Hesabı",
          "Müşteri Ödemeli",
        ],
        groups.map((g) => [
          report === "purpose"
            ? purposes[g.key] || g.key
            : report === "service"
              ? services[g.key] || g.key
              : g.key,
          g.count,
          g.total.toFixed(2),
          g.account.toFixed(2),
          g.customer.toFixed(2),
        ]),
      );
    downloadCsv(
      "JYSK-Siparişler",
      [
        "Sipariş",
        "Kaynak Talep",
        "Oluşturan Ad Soyad",
        "Oluşturan E-posta",
        "Mağaza",
        "Hizmet",
        "Durum",
        "Sipariş Amacı",
        "Ödeme Sorumlusu",
        "Ödeme Yöntemi",
        "Tutar",
        "JYSK Payı (%5)",
        "Kazanılan JYSK Payı",
        "Finansal Toplama Dahil",
        "Fatura",
        "Hesap Ödeme Durumu",
      ],
      filtered.map((o) => [
        ref(o._id),
        ref(o.sourceRequestId),
        o.createdBy?.name || o.employee?.name || "",
        o.createdBy?.email || "",
        o.storeId,
        services[o.service],
        statuses[o.viewStatus || o.status],
        purposes[o.orderPurpose || o.orderType] || o.orderType,
        payer(o.payer),
        methods[o.paymentMethod],
        Number(o.pricing?.price || 0).toFixed(2),
        Number(o.rebate || 0).toFixed(2),
        Number(o.earnedRebate || 0).toFixed(2),
        o.status === -1 ? "Hayır" : "Evet",
        o.invoiceId,
        o.accountPaymentStatus === "paid"
          ? "Ödendi"
          : o.payer === "account"
            ? "Ödeme Bekleniyor"
            : "—",
      ]),
    );
  };
  const select = (key, label, options) => (
    <label className={s.field}>
      {label}
      <select
        value={filters[key]}
        onChange={(e) =>
          setFilters((f) => ({
            ...f,
            [key]: e.target.value,
          }))
        }
      >
        <option value="">Tümü</option>
        {options.map(([value, text]) => (
          <option key={value} value={value}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
  const tabs = tabsForRole(ctx?.role);
  return (
    <>
      {smsConfirmation && <div style={{ position: "fixed", inset: 0, zIndex: 2000, background: "#132b4866", display: "grid", placeItems: "center", padding: 20 }}>
        <section role="dialog" aria-modal="true" aria-label="SMS gönderim onayı" className={s.card} style={{ maxWidth: 480 }}>
          <h2>SMS gönderimini onaylayın</h2><p>{smsConfirmation.message}</p>
          <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
            <button className={s.secondary} onClick={() => setSmsConfirmation(null)}>Vazgeç</button>
            <button className={s.button} onClick={() => { const extra = smsConfirmation.extra; setSmsConfirmation(null); updateRequest(extra, true); }}>Onayla ve SMS gönder</button>
          </div>
        </section>
      </div>}

      <div className={s.shell}>
        <main className={s.container}>
          <header className={s.heading}>
            <div>
              <span className={s.brand}>Taşımacım · JYSK</span>
              <h1>
                {ctx?.role === "finance"
                  ? "Finans paneli"
                  : "Mağaza teslimat yönetimi"}
              </h1>
              <p className={s.muted}>
                {ctx?.role === "finance"
                  ? "Tüm JYSK mağazalarının teslimat ve mali kayıtları"
                  : "Teslimat bilgilerini bir kez toplayın, talepleri ve siparişleri birlikte izleyin."}
              </p>
            </div>
            {detail?.kind === "request" &&
              (ctx?.canConvert ||
                (ctx?.role === "store" &&
                  detail.value.paymentResponsibility === "customer")) && (
                <button
                  className={s.button}
                  onClick={convert}
                  disabled={
                    busy ||
                    !!detail.value.orderId ||
                    detail.value.status === "cancelled" ||
                    detail.value.status === "order_created" ||
                    detail.value.stockStatus !== "stock_arrived" ||
                    (detail.value.productIssues || []).some(
                      (i) => i.status !== "resolved",
                    )
                  }
                >
                  {detail.value.paymentResponsibility === "customer"
                    ? "Sipariş bağlantısı gönder"
                    : "Siparişe dönüştür"}
                </button>
              )}
          </header>
          <nav className={s.tabs} aria-label="JYSK bölümleri">
            {tabs.map(([key, title]) => (
              <button
                key={key}
                className={`${s.secondary} ${tab === key ? s.active : ""}`}
                disabled={busy}
                onClick={() => changeTab(key)}
              >
                {title}
              </button>
            ))}
          </nav>
          {error && (
            <div className={s.error} role="alert">
              {error}
            </div>
          )}
          {notice && (
            <div className={s.notice} role="status">
              {notice}
            </div>
          )}
          {busy && (
            <p className={s.muted} role="status">
              Yükleniyor…
            </p>
          )}
          {detail ? (
            <>
              <button
                className={s.secondary}
                style={{
                  marginBottom: 20,
                }}
                onClick={() => {
                  setDetail(null);
                  run(() => load(tab));
                }}
              >
                ← Listeye dön
              </button>
              {detail.kind === "request" ? (
                <RequestDetail
                  request={detail.value}
                  readonly={ctx?.role === "finance"}
                  canConvert={ctx?.canConvert}
                  busy={busy}
                  update={updateRequest}
                  convert={convert}
                  openOrder={openOrder}
                />
              ) : detail.kind === "invoice" ? (
                <section className={s.card}>
                  <h2>{detail.value._id}</h2>
                  <Summary
                    rows={[
                      ["Dönem", detail.value.period],
                      ["Tutar", money(detail.value.amountCents / 100)],
                      ["Vade", detail.value.due],
                      ["Ödeme durumu", statuses[detail.value.paymentStatus]],
                      ["Ödeme tarihi", detail.value.payment?.date],
                      ["Referans", detail.value.payment?.reference],
                    ]}
                  />
                  <h3>Dahil edilen siparişler</h3>
                  <div className={s.row}>
                    {(Array.isArray(detail.value.orderIds)
                      ? detail.value.orderIds
                      : []
                    ).map((id) => (
                      <button
                        key={id}
                        className={s.secondary}
                        onClick={() => openOrder(id)}
                      >
                        #{ref(id)}
                      </button>
                    ))}
                  </div>
                </section>
              ) : (
                <OrderDetail
                  order={detail.value}
                  finance={ctx?.role === "finance"}
                  openRequest={openRequest}
                />
              )}
            </>
          ) : tab === "overview" ? (
            <section className={s.card}>
              <h2>Genel bakış</h2>
              <p className={s.muted}>Tüm JYSK mağazaları · Tüm zamanlar</p>
              <Summary rows={[
                ["Kazanılan toplam JYSK payı (%5)", overview ? money(overview.earnedRebate) : "—"],
                ["Beklenen JYSK payı", overview ? money(overview.pendingRebate) : "—"],
                ["Tamamlanan siparişler", overview?.completedOrders ?? "—"],
              ]} />
              <p className={s.muted}>Kazanılan pay, tamamlanan siparişlerde kayıtlı iade tutarıdır. Devam eden siparişler beklenen payda gösterilir; iptaller dahil edilmez. Kayıtlı payı olmayan siparişler için kazanç hesaplanmaz.</p>
              <button className={s.secondary} disabled={busy} onClick={() => run(() => load("overview"))}>Yenile</button>
            </section>
          ) : tab === "invite" && ctx ? (
            <Invitation
              ctx={ctx}
              done={() => {
                setNotice(
                  "SMS gönderim kaydı oluşturuldu. Durumu SMS geçmişinden izleyebilirsiniz.",
                );
                changeTab("sms");
              }}
              run={run}
              busy={busy}
            />
          ) : (
            <>
              <section className={s.card}>
                <div className={s.fields}>
                  <label className={s.field}>
                    Ara
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Numara, müşteri veya mağaza"
                    />
                  </label>
                  {tab === "requests" && (
                    <>
                      {select("payer", "Ödeme sorumlusu", [
                        ["account", "JYSK hesabı"],
                        ["customer", "Müşteri"],
                      ])}
                      {select("stockStatus", "Stok", [
                        ["stock_pending", "Stok bekleniyor"],
                        ["stock_arrived", "Stok geldi"],
                      ])}
                    </>
                  )}
                  {(tab === "requests" || tab === "sms") && (
                    <button
                      className={s.button}
                      disabled={busy}
                      onClick={() => run(() => load(tab))}
                    >
                      Uygula
                    </button>
                  )}
                  {(tab === "orders" || tab === "finance") && (
                    <>
                      <label className={s.field}>
                        Başlangıç
                        <input
                          type="date"
                          value={filters.from}
                          onChange={(e) =>
                            setFilters((f) => ({
                              ...f,
                              from: e.target.value,
                            }))
                          }
                        />
                      </label>
                      <label className={s.field}>
                        Bitiş
                        <input
                          type="date"
                          min={filters.from}
                          value={filters.to}
                          onChange={(e) =>
                            setFilters((f) => ({
                              ...f,
                              to: e.target.value,
                            }))
                          }
                        />
                      </label>
                      {select("dateBy", "Tarih türü", [
                        ["created", "Oluşturulma tarihi"],
                        ["scheduled", "Seçilen teslimat tarihi"],
                      ])}
                      {select(
                        "store",
                        "Mağaza",
                        (ctx?.stores || []).map((st) => [
                          st.storeId,
                          `${st.storeId} · ${st.label}`,
                        ]),
                      )}
                      {select(
                        "city",
                        "Şehir",
                        [...new Set((ctx?.stores || []).map((st) => st.city))]
                          .filter(Boolean)
                          .map((city) => [
                            city,
                            ctx?.stores.find((st) => st.city === city)
                              ?.cityLabel ||
                              {
                                34: "İstanbul",
                                16: "Bursa",
                                35: "İzmir",
                                9: "Aydın",
                                54: "Sakarya",
                                6: "Ankara",
                                48: "Muğla",
                              }[city] ||
                              city,
                          ]),
                      )}
                      {select(
                        "status",
                        "Sipariş durumu",
                        [
                          -1,
                          0,
                          1,
                          2,
                          3,
                          4,
                          10,
                          "arrived_pickup",
                          "arrived_intermediate",
                          "arrived_delivery",
                        ].map((k) => [k, statuses[k]]),
                      )}
                      {select("payer", "Ödeme sorumlusu", [
                        ["account", "JYSK hesabı"],
                        ["customer", "Müşteri"],
                      ])}
                      {select(
                        "payment",
                        "Ödeme yöntemi",
                        Object.entries(methods).filter(([key]) => key !== "2"),
                      )}
                      {select(
                        "purpose",
                        "Sipariş amacı",
                        JYSK_ORDER_TYPES.map((p) => [p.value, p.label]),
                      )}
                      {select("service", "Hizmet", [
                        ["immediate_scheduled", "Hemen / Planlı"],
                        ["immediate", "Hemen"],
                        ["scheduled", "Planlı"],
                        ["flexible", "Esnek"],
                      ])}
                      <button
                        className={s.button}
                        disabled={busy}
                        onClick={() => run(() => load(tab))}
                      >
                        Uygula
                      </button>
                      <button
                        className={s.secondary}
                        disabled={busy}
                        onClick={() => {
                          setFilters({
                            from: "",
                            to: "",
                            store: "",
                            city: "",
                            status: "",
                            payer: "",
                            payment: "",
                            purpose: "",
                            service: "",
                            dateBy: "created",
                          });
                          setSearch("");
                          run(async () => {
                            const data = await jyskFetch("orders");
                            setRows(data.rows);
                          });
                        }}
                      >
                        Sıfırla
                      </button>
                    </>
                  )}
                  {tab === "invoices" && (
                    <>
                      <label className={s.field}>
                        Dönem
                        <input
                          type="month"
                          value={invoicePeriod}
                          onChange={(e) => setInvoicePeriod(e.target.value)}
                        />
                      </label>
                      <label className={s.field}>
                        Ödeme durumu
                        <select
                          value={invoiceStatus}
                          onChange={(e) => setInvoiceStatus(e.target.value)}
                        >
                          <option value="">Tümü</option>
                          {["awaiting_payment", "overdue", "paid"].map((k) => (
                            <option key={k} value={k}>
                              {statuses[k]}
                            </option>
                          ))}
                        </select>
                      </label>
                    </>
                  )}
                </div>
              </section>
              {(tab === "orders" || tab === "finance") && (
                <div className={s.metrics}>
                  <div className={s.card}>
                    <span className={s.muted}>
                      Sipariş sayısı · İptaller hariç
                    </span>
                    <strong>{financial.length}</strong>
                  </div>
                  <div className={s.card}>
                    <span className={s.muted}>Toplam tutar</span>
                    <strong>{money(total)}</strong>
                  </div>
                  {tab === "finance" && (
                    <>
                      <div className={s.card}>
                        <span className={s.muted}>JYSK hesabı</span>
                        <strong>
                          {money(
                            financial
                              .filter((o) => o.payer === "account")
                              .reduce((n, o) => n + o.pricing.price, 0),
                          )}
                        </strong>
                      </div>
                      <div className={s.card}>
                        <span className={s.muted}>Müşteri ödemeli</span>
                        <strong>
                          {money(
                            financial
                              .filter((o) => o.payer !== "account")
                              .reduce((n, o) => n + o.pricing.price, 0),
                          )}
                        </strong>
                      </div>
                    </>
                  )}
                </div>
              )}
              <section className={s.card}>
                <div className={s.heading}>
                  <h2>{tabs.find((t) => t[0] === tab)?.[1]}</h2>
                  {["orders", "finance", "invoices"].includes(tab) && (
                    <button className={s.secondary} onClick={exportReport}>
                      CSV indir
                    </button>
                  )}
                </div>
                {tab === "finance" && (
                  <>
                    <div className={s.tabs}>
                      {[
                        ["stores", "Mağaza harcamaları"],
                        ["purpose", "Sipariş amacına göre"],
                        ["service", "Hizmete göre"],
                        ["orders", "Sipariş mali verileri"],
                      ].map(([key, text]) => (
                        <button
                          key={key}
                          className={`${s.secondary} ${report === key ? s.active : ""}`}
                          onClick={() => setReport(key)}
                        >
                          {text}
                        </button>
                      ))}
                    </div>
                    <p className={s.muted}>
                      Devam eden siparişler beklenen tutarı gösterir. İptaller
                      mali toplamlara dahil edilmez.
                    </p>
                  </>
                )}
                {tab === "requests" ? (
                  <Table
                    headers={[
                      "Talep",
                      "Müşteri",
                      "Mağaza / kaynak",
                      "Ödeme",
                      "Stok",
                      "Ürün doğrulaması",
                      "Sipariş",
                      "İşlem",
                    ]}
                    rows={filtered.map((r) => [
                      ref(r._id),
                      <React.Fragment key="1">
                        {r.full_name}
                        <p className={s.muted}>{r.phone}</p>
                      </React.Fragment>,
                      <React.Fragment key="2">
                        {r.store_label}
                        <p className={s.muted}>
                          {r.source === "sms"
                            ? "SMS bağlantısı"
                            : show(r.source)}
                        </p>
                      </React.Fragment>,
                      payer(r.paymentResponsibility),
                      <Badge key="4">
                        {(r.productIssues || []).some(issue => issue.status !== "resolved") ? "İnceleme bekliyor" : statuses[r.stockStatus || r.status]}
                      </Badge>,
                      (r.productIssues || []).some(
                        (i) => i.status !== "resolved",
                      ) ? (
                        <span className={s.warningBadge}>
                          ⚠ Ürün doğrulaması gerekli
                        </span>
                      ) : (
                        "Sorun yok"
                      ),
                      r.orderId ? (
                        <button
                          className={s.link}
                          onClick={() => openOrder(r.orderId)}
                        >
                          #{ref(r.orderId)} · {statuses[r.linkedOrderStatus]}
                        </button>
                      ) : r.status === "order_created" ? (
                        "Sipariş bağlantısı doğrulanmalı"
                      ) : statuses[r.status] === "İptal Edildi" ? (
                        "İptal Edildi"
                      ) : (
                        "Sipariş oluşturulmadı"
                      ),
                      <button
                        className={s.secondary}
                        onClick={() => openRequest(r._id)}
                        key="7"
                      >
                        Aç
                      </button>,
                    ])}
                  />
                ) : tab === "sms" ? (
                  <Table
                    headers={[
                      "Telefon / tarih",
                      "Gönderen / çalışan",
                      "Bağlantı nedeni",
                      "Ödeme",
                      "SMS türü",
                      "Gönderim / iletim",
                      "Form",
                      "Mesaj",
                    ]}
                    rows={filtered.map((r) => [
                      <React.Fragment key="0">
                        {r.phone}
                        <p className={s.muted}>{date(r.createdAt)}</p>
                      </React.Fragment>,
                      r.employee
                        ? [r.employee.name, r.employee.id].filter(Boolean).join(" · ")
                        : `${r.sender?.name || "—"} · ${r.sender?.email || "—"}`,
                      r.linkReason === "sale" ? "Satış" : "Müşteri siparişi",
                      payer(r.paymentResponsibility),
                      smsTypes[r.type] || r.type,
                      <React.Fragment key="5">
                        {statuses[r.status] || r.status}
                        <p className={s.muted}>
                          {r.deliveryStatus
                            ? statuses[r.deliveryStatus] || r.deliveryStatus
                            : r.status === "failed"
                              ? "SMS gönderilemedi"
                              : ""}
                        </p>
                      </React.Fragment>,
                      r.requestId ? (
                        <button
                          className={s.link}
                          onClick={() => openRequest(r.requestId)}
                        >
                          #{ref(r.requestId)}
                        </button>
                      ) : r.type === "invitation" && r.status === "failed" ? (
                        "Form bağlantısı gönderilemedi"
                      ) : (
                        "Form bekleniyor"
                      ),
                      <details key="7">
                        <summary>Mesajı aç</summary>
                        <p>{r.message}</p>
                      </details>,
                    ])}
                  />
                ) : tab === "invoices" ? (
                  <Table
                    headers={[
                      "Fatura",
                      "Dönem",
                      "Sipariş sayısı",
                      "Tutar",
                      "Vade",
                      "Durum",
                      "İşlem",
                    ]}
                    rows={invoices.map((i) => [
                      i._id,
                      i.period,
                      Array.isArray(i.orderIds) ? i.orderIds.length : 0,
                      money(i.amountCents / 100),
                      i.due,
                      <Badge key="5">{statuses[i.paymentStatus]}</Badge>,
                      <button
                        className={s.secondary}
                        onClick={() =>
                          setDetail({
                            kind: "invoice",
                            value: i,
                          })
                        }
                        key="6"
                      >
                        Aç
                      </button>,
                    ])}
                  />
                ) : tab === "finance" && report !== "orders" ? (
                  <Table
                    headers={[
                      report === "purpose"
                        ? "Sipariş amacı"
                        : report === "service"
                          ? "Hizmet"
                          : "Mağaza",
                      "Sipariş sayısı",
                      "Toplam",
                      "JYSK hesabı",
                      "Müşteri ödemeli",
                    ]}
                    rows={groups.map((g) => [
                      report === "purpose"
                        ? purposes[g.key] || g.key
                        : report === "service"
                          ? services[g.key]
                          : `${g.key} · ${ctx?.stores.find((st) => st.storeId === g.key)?.label || ""}`,
                      g.count,
                      money(g.total),
                      money(g.account),
                      money(g.customer),
                    ])}
                  />
                ) : (
                  <Table
                    headers={[
                      "Sipariş",
                      "Mağaza",
                      "Hizmet",
                      "Durum",
                      "Ödeme",
                      "Tutar",
                      "JYSK Payı (%5)",
                      "İşlem",
                    ]}
                    rows={filtered.map((o) => [
                      ref(o._id),
                      o.storeId,
                      services[o.service],
                      <Badge key="3">
                        {statuses[o.viewStatus || o.status] || o.status}
                      </Badge>,
                      <React.Fragment key="4">
                        {payer(o.payer)}
                        <p className={s.muted}>{methods[o.paymentMethod]}</p>
                      </React.Fragment>,
                      money(o.pricing?.price),
                      money(o.rebate),
                      <button
                        className={s.secondary}
                        onClick={() => openOrder(o._id)}
                        key="6"
                      >
                        Aç
                      </button>,
                    ])}
                  />
                )}
                <p className={s.muted}>
                  {pagination
                    ? pagination.total
                    : tab === "invoices"
                      ? invoices.length
                      : filtered.length}{" "}
                  kayıt
                </p>
                {pagination && pagination.totalPages > 1 && (
                  <div className={s.row}>
                    <button
                      className={s.secondary}
                      disabled={busy || pagination.page <= 1}
                      onClick={() => run(() => load(tab, pagination.page - 1))}
                    >
                      Önceki sayfa
                    </button>
                    <span>
                      {pagination.page} / {pagination.totalPages}
                    </span>
                    <button
                      className={s.secondary}
                      disabled={
                        busy || pagination.page >= pagination.totalPages
                      }
                      onClick={() => run(() => load(tab, pagination.page + 1))}
                    >
                      Sonraki sayfa
                    </button>
                  </div>
                )}
              </section>
            </>
          )}
        </main>
      </div>
    </>
  );
}
function Invitation() {
  return null;
}
function RequestDetail({
  request: r,
  readonly,
  canConvert,
  busy,
  update,
  convert,
  openOrder,
}) {
  const [resolution, setResolution] = useState({});
  const [quantities, setQuantities] = useState({});
  const [codes, setCodes] = useState({});
  useEffect(() => {
    setQuantities({});
    setCodes({});
  }, [r._id, r.updatedAt]);
  const open = (r.productIssues || []).some((i) => i.status !== "resolved");
  return (
    <>
      {open && (
        <p className={s.warningBadge} role="status">
          İnceleme bekliyor — müşteri ürünle ilgili sorun bildirdi.
        </p>
      )}
      <section className={s.card}>
        <h2>Teslimat talebi #{ref(r._id)}</h2>
        <Summary
          rows={[
            ["Mağaza", `${r.store_reference_id} · ${r.store_label}`],
            ["Kaynak", r.source === "sms" ? "SMS bağlantısı" : r.source],
            ["Durum", statuses[r.status]],
            ["Ödeme sorumlusu", payer(r.paymentResponsibility)],
            ["Stok", statuses[r.stockStatus || r.status]],
            [
              "Ürün doğrulaması",
              open ? "Ürün doğrulaması bekleniyor" : "Tamamlandı",
            ],
            ["Müşteri", r.full_name],
            ["Telefon", r.phone],
            ["E-posta", r.email],
            ["Teslimat adresi", r.delivery_address?.label],
            [
              "Bina / Daire / Kat",
              `${r.delivery_address?.building || "—"} / ${r.delivery_address?.unit || "—"} / ${r.delivery_address?.floor || "0"}`,
            ],
          ]}
        />
      </section>
      {!readonly && (
        <section className={s.card}>
          <h2>Mağaza bilgileri · Dahili</h2>
          <Summary
            rows={[
              [
                "Bağlantı nedeni",
                r.linkReason === "sale" ? "Satış" : "Müşteri siparişi",
              ],
              ["Hizmet", services[r.service]],
              ["Sipariş amacı", purposes[r.orderPurpose]],
              [
                "Çalışan",
                r.employee
                  ? [r.employee.id, r.employee.name].filter(Boolean).join(" · ")
                  : `${r.sender?.name || "—"} · ${r.sender?.email || "—"}`,
              ],
              ["Mağaza notu", r.internalNote],
              [
                "İç taşıma tercihi",
                r.internalCarrying === "apartment"
                  ? "Daireye taşıma talebi"
                  : "Araçtan teslim",
              ],
            ]}
          />
        </section>
      )}
      {!readonly && !r.paymentResponsibility && (
        <section className={s.card}>
          <h2>Ödeme sorumluluğunu doğrulayın</h2>
          <p className={s.muted}>
            Eski talepte ödeme sorumluluğu kaydedilmemiş.
          </p>
          <div className={s.row}>
            <button
              className={s.secondary}
              disabled={busy}
              onClick={() => update({ paymentResponsibility: "customer" })}
            >
              Müşteri ödemeli
            </button>
            <button
              className={s.secondary}
              disabled={busy}
              onClick={() => update({ paymentResponsibility: "account" })}
            >
              JYSK hesabı
            </button>
          </div>
        </section>
      )}
      <section className={s.card}>
        <h2>Ürünler ve müşteri bildirimleri</h2>
        {!!r.productReviews?.length && (
          <details className={s.product}>
            <summary>Orijinal müşteri kontrolleri</summary>
            {r.productReviews.map((review, i) => (
              <p key={i}>
                {review.barcode} · {review.quantity} adet ·{" "}
                {review.confirmed
                  ? "Müşteri onayladı"
                  : "Müşteri sorun bildirdi"}
              </p>
            ))}
          </details>
        )}
        {(r.barcodes || []).map((p) => (
          <div className={s.product} key={p.barcode}>
            <img
              className={s.productImage}
              src={`https://cdn1.jysk.com/getimage/wd3.medium/${encodeURIComponent(p.barcode)}`}
              alt={p.product_name || p.barcode}
              onError={(e) => {
                e.currentTarget.style.visibility = "hidden";
              }}
            />
            <strong>{p.product_name}</strong>
            <p className={s.muted}>
              {p.barcode} · {p.quantity} adet ·{" "}
              {p.customerConfirmed ? "Müşteri onayladı" : "Müşteri kontrolü"}
            </p>
            {!readonly && !r.orderId && r.status !== "order_created" && (
              <label className={s.field}>
                Doğrulanmış ürün kodu
                <input
                  maxLength={7}
                  inputMode="numeric"
                  value={codes[p.barcode] ?? p.barcode}
                  onChange={(e) =>
                    setCodes((v) => ({
                      ...v,
                      [p.barcode]: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                />
              </label>
            )}
            {!readonly && !r.orderId && r.status !== "order_created" && (
              <label className={s.field}>
                Doğrulanmış adet
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={quantities[p.barcode] ?? p.quantity}
                  onChange={(e) =>
                    setQuantities((v) => ({
                      ...v,
                      [p.barcode]: Number(e.target.value),
                    }))
                  }
                />
              </label>
            )}
          </div>
        ))}
        {(r.productIssues || []).map((issue) => (
          <div
            key={issue.id}
            className={issue.status === "resolved" ? s.notice : s.error}
          >
            <strong>
              {issue.status === "resolved"
                ? "Doğrulandı"
                : "Ürün doğrulaması bekleniyor"}
            </strong>
            <p>
              {issue.originalProduct?.barcode || issue.barcode} ·{" "}
              {issue.explanation}
            </p>
            {issue.resolution && <p>Çözüm: {issue.resolution}</p>}
            {issue.status !== "resolved" && !readonly && (
              <>
                <label className={s.field}>
                  Doğrulama açıklaması
                  <textarea
                    value={resolution[issue.id] || ""}
                    onChange={(e) =>
                      setResolution((v) => ({
                        ...v,
                        [issue.id]: e.target.value,
                      }))
                    }
                  />
                </label>
                <button
                  className={s.secondary}
                  disabled={busy || !resolution[issue.id]?.trim()}
                  onClick={() =>
                    update({
                      resolveIssue: issue.id,
                      resolution: resolution[issue.id],
                      barcodes: r.barcodes.map((item) => ({
                        ...item,
                        barcode: codes[item.barcode] || item.barcode,
                        quantity: quantities[item.barcode] ?? item.quantity,
                      })),
                    })
                  }
                >
                  Düzelt ve doğrula
                </button>
              </>
            )}
          </div>
        ))}
        {!readonly && !r.orderId && r.status !== "order_created" && (
          <button
            className={s.secondary}
            disabled={busy}
            onClick={() =>
              update({
                barcodes: r.barcodes.map((item) => ({
                  ...item,
                  barcode: codes[item.barcode] || item.barcode,
                  quantity: quantities[item.barcode] ?? item.quantity,
                })),
              })
            }
          >
            Ürün adetlerini kaydet
          </button>
        )}
      </section>
      <section className={s.card}>
        <h2>Fişler</h2>
        <div className={s.list}>
          {(r.receipt_images || []).map((f, i) => (
            <a
              key={i}
              href={f.url || `data:${f.mime_type};base64,${f.base64}`}
              target="_blank"
              rel="noreferrer"
            >
              {f.name || `Fiş ${i + 1}`}
            </a>
          ))}
          {!r.receipt_images?.length && (
            <p className={s.muted}>Fiş eklenmemiş.</p>
          )}
        </div>
      </section>
      <section className={s.card}>
        <h2>Sipariş bağlantısı</h2>
        {r.orderId ? (
          <button className={s.secondary} onClick={() => openOrder(r.orderId)}>
            #{ref(r.orderId)} · {statuses[r.linkedOrderStatus]} · Siparişi aç
          </button>
        ) : r.status === "order_created" ? (
          <div className={s.notice}>
            Bu eski talep daha önce siparişe dönüştürülmüş. Sipariş bağlantısı
            operasyon tarafından doğrulanmalıdır.
          </div>
        ) : (
          <>
            <p className={s.muted}>
              Henüz sipariş oluşturulmadı. Stok hazır ve tüm ürün bildirimleri
              doğrulanmış olmalıdır.
            </p>
            {!readonly && (
              <div className={s.row}>
                <button
                  className={s.secondary}
                  disabled={busy || r.status === "cancelled"}
                  onClick={() =>
                    update({
                      stockStatus:
                        r.stockStatus === "stock_arrived"
                          ? "stock_pending"
                          : "stock_arrived",
                    })
                  }
                >
                  {r.stockStatus === "stock_arrived"
                    ? "Stok bekleniyor olarak işaretle"
                    : "Stok geldi olarak işaretle"}
                </button>
                <button
                  className={s.secondary}
                  disabled={busy || r.status === "cancelled"}
                  onClick={() => {
                    if (window.confirm("Teslimat talebi iptal edilsin mi?"))
                      update({ cancel: true });
                  }}
                >
                  Talebi iptal et
                </button>
              </div>
            )}
          </>
        )}
      </section>
      {!readonly && !!r.history?.length && (
        <section className={s.card}>
          <h2>Değişiklik geçmişi</h2>
          {r.history.map((event, i) => (
            <details key={i} className={s.product}>
              <summary>
                {date(event.at)} · {event.actorName || "Operasyon"}
              </summary>
              <p>
                Önceki adetler:{" "}
                {(event.before?.barcodes || [])
                  .map((p) => `${p.barcode}: ${p.quantity}`)
                  .join(", ")}
              </p>
              <p>
                Yeni adetler:{" "}
                {(event.changes?.barcodes || [])
                  .map((p) => `${p.barcode}: ${p.quantity}`)
                  .join(", ") || "Değişmedi"}
              </p>
              <p>Stok: {statuses[event.changes?.stockStatus] || "Değişmedi"}</p>
            </details>
          ))}
        </section>
      )}
    </>
  );
}
function OrderDetail({ order: o, finance, openRequest }) {
  const address = (a) =>
    a
      ? `${a.Label || a.address || ""} · Bina ${a.building || "—"} / Daire ${a.unit || a.door || "—"} / Kat ${a.floor || "0"} · ${a.elevator ? "Asansör kullanılabilir" : "Asansör kullanılamaz"}`
      : "—";
  return (
    <>
      <section className={s.card}>
        <h2>Sipariş #{ref(o._id)}</h2>
        <Summary
          rows={[
            ["Hizmet", services[o.service]],
            ["Durum", statuses[o.viewStatus || o.status]],
            [
              "Oluşturulma tarihi",
              date(
                o.createdAt || parseInt(String(o._id).slice(0, 8), 16) * 1000,
              ),
            ],
          ]}
        />
      </section>
      <section className={s.card}>
        <h2>Müşteri ve adresler</h2>
        <Summary
          rows={[
            ["Müşteri", o.to?.contactName],
            ["Telefon", o.to?.contactNO],
            ["E-posta", o.to?.email],
            ["Alış adresi", address(o.from)],
            ["Teslimat adresi", address(o.to)],
          ]}
        />
      </section>
      <section className={s.card}>
        <h2>Taşınacak ürünler</h2>
        {(o.items || []).map((p, i) => (
          <div className={s.product} key={i}>
            <strong>{p.name || p.product_name}</strong>
            <p className={s.muted}>
              {p.barcode} · {p.quantity || 1} adet ·{" "}
              {p.boxes?.length
                ? `${p.boxes.length} paket`
                : "Paket bilgisi yok"}
            </p>
          </div>
        ))}
      </section>
      <section className={s.card}>
        <h2>Tarih ve teslimat planı</h2>
        <Summary
          rows={[
            ["Seçilen tarih", date(o.date)],
            [
              "Onaylı randevu",
              date(o.confirmedDate || o.scheduledStartDate || o.scheduledDate),
            ],
          ]}
        />
        {o.service === "flexible" && (
          <>
            <p className={s.muted}>Müşterinin uygunluk takvimi</p>
            <div className={s.calendar}>
              {(o.times || []).map((chosen, i) =>
                chosen ? (
                  <div className={s.slot} key={i}>
                    {new Date(
                      Number(o.timeStartDate) + Math.floor(i / 3) * 86400000,
                    ).toLocaleDateString("tr-TR")}
                    <br />
                    {["10:00–14:00", "14:00–18:00", "18:00–22:00"][i % 3]}
                  </div>
                ) : null,
              )}
            </div>
          </>
        )}
      </section>
      <section className={s.card}>
        <h2>Araç, taşıma ve ödeme</h2>
        <Summary
          rows={[
            [
              "Araç",
              {
                0: "Motosiklet",
                2: "Doblo",
                3: "Minivan",
                4: "Panelvan",
                5: "Kamyonet",
                6: "Esnek",
              }[o.vehicle] || o.vehicle,
            ],
            [
              "Taşıma yardımı",
              o.additionals?.[4]
                ? `${o.additionals[4]} yardımcı / taşıma seçimi`
                : "Ek taşıma yardımı yok",
            ],
            ["Ödeme sorumlusu", payer(o.payer)],
            ["Ödeme yöntemi", methods[o.paymentMethod]],
            ["Toplam tutar", money(o.pricing?.price)],
            ["JYSK payı (%5)", money(o.rebate)],
            ["Kazanılan JYSK payı", money(o.earnedRebate)],
            ["Fatura", o.invoiceId],
            ["Ödeme durumu", o.paid ? "Ödendi" : "Ödeme Bekleniyor"],
          ]}
        />
      </section>
      <section className={s.card}>
        <h2>Oluşturan hesap ve kaynak</h2>
        <Summary
          rows={[
            [
              "Oluşturan",
              o.createdByType === "customer"
                ? "Müşteri tarafından oluşturuldu"
                : o.createdBy
                  ? `${o.createdBy.name} · ${o.createdBy.email}`
                  : `${o.employee?.id || "—"} · ${o.employee?.name || "—"}`,
            ],
            [
              "Kaynak talep",
              o.sourceRequestId ? (
                <button
                  className={s.link}
                  onClick={() => openRequest(o.sourceRequestId)}
                >
                  #{ref(o.sourceRequestId)} · Talebi aç
                </button>
              ) : (
                "Doğrudan sipariş"
              ),
            ],
          ]}
        />
      </section>
      {!finance && (
        <section className={s.card}>
          <h2>Takip ve sürücü</h2>
          <p>{o.driverInfo?.name || "Sürücü henüz atanmadı"}</p>
          {o.driverInfo?.phone && (
            <p>
              <a className={s.link} href={`tel:${o.driverInfo.phone}`}>
                {o.driverInfo.phone}
              </a>
            </p>
          )}
          <a
            className={s.button}
            href={`/jysk-takip/${o._id}`}
            target="_blank"
            rel="noreferrer"
          >
            Siparişi takip et
          </a>
        </section>
      )}
    </>
  );
}
